import { Archive, ArrowLeft, Download, Pencil, Plus, Trash2, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { downloadFileFromUrl } from "../../../lib/utils";
import {
  getPatientConsultationsPro,
  getPatientMedicalFilePro,
  getPatientPrescriptionsPro,
  getPatientVaccinationsPro,
  getConsultationPdfPro,
  getOrdonnancePdfPro,
  getMedicalFilePdfPro,
  getVaccinationCertificatePro,
  getRaisonsArchivage,
  archiverPatient,
  updateDossierMedicalInfoPro,
  type RaisonArchivage,
} from "../../../api/professionalPatient";
import { useAuth } from "../../../store/authEtablissementStore";
import PatientAccessOtpModal, { type ContactSureteDisponible } from "../../../components/PatientAccessOtpModal";
import FileAttachmentLink, { type FileAttachmentData } from "../../../components/ui/FileAttachment";
import { showToast } from "../../../components/ui/toastHotSonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { getProfessionsMetiers, type ProfessionMetier } from "../../../api/publicBenin";
import {
  BLOOD_GROUP_OPTIONS,
  ELECTROPHORESE_OPTIONS,
  UNSET_OPTION,
  withCurrentValue,
  emptyAntecedent,
  dateInputValue,
  extractRawMedFile,
  type RawAntecedent,
  type RawMedFile,
} from "../../../lib/dossierMedicalEdit";

function ageFromIso(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const years = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  return `${years} ans`;
}

function formatDateFr(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR");
}

/**
 * L'établissement n'a pas accès à `GET /patient/patients/:nip` ("Type de compte non autorisé
 * pour cette opération" — restriction permanente, pas liée à ACCES_DOSSIER_REQUIS). L'identité du
 * patient est donc reconstruite depuis la réponse du dossier médical (`informationsPersonnelles`),
 * seule source disponible pour ce profil.
 */
type PatientInfo = {
  nip: string;
  nom: string;
  prenom: string;
  email: string;
  phone: string;
  birth: string;
  age: string;
  blood: string;
  address: string;
  job: string;
  createur: string | null;
};

/** Shell affiché tant que l'accès au dossier n'a pas encore été accordé (identité inconnue). */
function minimalPatient(nip: string): PatientInfo {
  return {
    nip,
    nom: "",
    prenom: "",
    email: "",
    phone: "",
    birth: "",
    age: "—",
    blood: "",
    address: "",
    job: "",
    createur: null,
  };
}

/** `createur` peut être un NIP professionnel (10 chiffres) ou un id établissement (préfixe `etab_`). */
function describeCreateur(createur: string | null): string | null {
  if (!createur) return null;
  if (createur.startsWith("etab_")) return "Créé par un établissement";
  if (/^\d{10}$/.test(createur)) return `Créé par le professionnel ${createur}`;
  return `Créé par ${createur}`;
}

function extractPatientFromDossier(raw: unknown, fallbackNip: string): PatientInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const m = raw as Record<string, unknown>;
  const ip = (m.informationsPersonnelles as Record<string, unknown> | undefined) ?? {};
  const im = (m.informationsMedicales as Record<string, unknown> | undefined) ?? {};
  const snapshot = (m.snapshot as Record<string, unknown> | undefined) ?? {};
  const nip = String(m.patientNip ?? fallbackNip ?? "").trim();
  if (!nip) return null;
  const birth = String(ip.dateNaissance ?? snapshot.birth ?? "");
  return {
    nip,
    nom: String(ip.nom ?? "").toUpperCase(),
    prenom: String(ip.prenom ?? ""),
    email: String(ip.email ?? ""),
    phone: String(ip.telephone ?? ""),
    birth,
    age: ageFromIso(birth),
    blood: String(im.groupeSanguin ?? snapshot.gs ?? ""),
    address: String(ip.adresse ?? snapshot.address ?? ""),
    job: String(ip.profession ?? ""),
    createur: typeof m.createur === "string" ? m.createur : null,
  };
}

type MedFile = {
  allergies: string[];
  intolerances: string[];
  antecedents: string[];
  groupeSanguin: string;
  poids: number | null;
  taille: number | null;
  electrophorese: string;
};

function extractMedFile(raw: unknown): MedFile {
  const m = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const im = (m.informationsMedicales as Record<string, unknown> | undefined) ?? {};
  const toStrArray = (v: unknown): string[] => {
    if (!Array.isArray(v)) return [];
    return v.map((x) => (typeof x === "string" ? x : String((x as Record<string, unknown>)?.description ?? (x as Record<string, unknown>)?.type ?? x)));
  };
  return {
    allergies: toStrArray(im.allergies),
    intolerances: toStrArray(im.intolerances),
    antecedents: toStrArray(im.antecedents),
    groupeSanguin: String(im.groupeSanguin ?? ""),
    poids: typeof im.poids === "number" ? im.poids : null,
    taille: typeof im.taille === "number" ? im.taille : null,
    electrophorese: String(im.electrophorese ?? ""),
  };
}

type ConsultEntry = {
  id?: string;
  selectionKey: string;
  label: string;
  doctor: string;
  professionnelNip?: string;
  motif?: string;
  observations?: string;
  etatSortie?: string;
  statut?: string;
  analyses?: string[];
  resultats?: { disease: string; note: string };
  ordonnances?: { medicament: string; note: string }[];
  documents?: FileAttachmentData[];
};

function mapConsult(c: unknown, index: number): ConsultEntry {
  const o = c && typeof c === "object" ? (c as Record<string, unknown>) : {};
  const rawDate = o.date ?? o.createdAt ?? o.updatedAt;
  const parsed = typeof rawDate === "string" ? new Date(rawDate) : null;
  const label =
    parsed && !Number.isNaN(parsed.getTime())
      ? parsed.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
      : "Consultation";
  const idRaw = o.id ?? o._id;
  const id =
    typeof idRaw === "string" ? idRaw : typeof idRaw === "number" && Number.isFinite(idRaw) ? String(idRaw) : undefined;
  const selectionKey = id ?? (typeof rawDate === "string" && rawDate ? rawDate : `idx-${index}`);

  const complement =
    o.complement && typeof o.complement === "object" && !Array.isArray(o.complement)
      ? (o.complement as Record<string, unknown>)
      : null;
  let analyses: string[] | undefined;
  let resultats: { disease: string; note: string } | undefined;
  let ordonnances: { medicament: string; note: string }[] | undefined;
  if (complement) {
    if (Array.isArray(complement.analyses)) {
      analyses = complement.analyses.map((a) => (typeof a === "string" ? a : String(a)));
    }
    if (complement.resultats && typeof complement.resultats === "object" && !Array.isArray(complement.resultats)) {
      const r = complement.resultats as Record<string, unknown>;
      resultats = { disease: String(r.disease ?? "—"), note: String(r.note ?? "") };
    }
    if (Array.isArray(complement.ordonnances)) {
      ordonnances = (complement.ordonnances as Record<string, unknown>[]).map((row) => ({
        medicament: String(row.medicament ?? ""),
        note: String(row.note ?? ""),
      }));
    }
  }

  let documents: FileAttachmentData[] | undefined;
  if (Array.isArray(o.documents)) {
    documents = (o.documents as Record<string, unknown>[])
      .map((d) => ({
        url: String(d.bucketUrl ?? d.url ?? ""),
        fileName: String(d.fileName ?? d.name ?? "Document"),
        mimeType: typeof d.mimeType === "string" ? d.mimeType : undefined,
      }))
      .filter((d) => d.url);
  }

  return {
    id,
    selectionKey,
    label,
    doctor:
      typeof o.professionnelNip === "string"
        ? o.professionnelNip
        : typeof o.professionnel_nip === "string"
          ? o.professionnel_nip
          : "—",
    professionnelNip: typeof o.professionnelNip === "string" ? o.professionnelNip : undefined,
    motif: typeof o.motif === "string" ? o.motif : undefined,
    observations: typeof o.observations === "string" ? o.observations : undefined,
    etatSortie: typeof o.etatSortie === "string" ? o.etatSortie : undefined,
    statut:
      typeof o.statutConsultation === "string"
        ? o.statutConsultation
        : typeof o.statut === "string"
          ? o.statut
          : undefined,
    analyses,
    resultats,
    ordonnances,
    documents,
  };
}

type OrdonnanceEntry = {
  id?: string;
  label: string;
  statut?: string;
  medicaments: { medicament: string; note?: string }[];
};

function mapOrdonnance(raw: unknown): OrdonnanceEntry {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const idRaw = o.id ?? o._id;
  const id = typeof idRaw === "string" ? idRaw : typeof idRaw === "number" ? String(idRaw) : undefined;
  const rawDate = o.datePrescription ?? o.date ?? o.createdAt;
  const parsed = typeof rawDate === "string" ? new Date(rawDate) : null;
  const label =
    parsed && !Number.isNaN(parsed.getTime())
      ? parsed.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
      : "Ordonnance";
  const medicamentsRaw = Array.isArray(o.medicaments) ? o.medicaments : [];
  const medicaments = medicamentsRaw
    .map((m) => {
      if (typeof m === "string") return { medicament: m };
      const mo = m as Record<string, unknown>;
      return {
        medicament: String(mo.medicament ?? mo.nom ?? mo.name ?? ""),
        note: typeof mo.note === "string" ? mo.note : typeof mo.posologie === "string" ? mo.posologie : undefined,
      };
    })
    .filter((m) => m.medicament);
  return {
    id,
    label,
    statut: typeof o.statut === "string" ? o.statut : undefined,
    medicaments,
  };
}

type VaccinationEntry = {
  id?: string;
  nom: string;
  date: string;
  dose?: string;
  lot?: string;
  prochaineDose?: string | null;
};

function mapVaccination(raw: unknown): VaccinationEntry {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const idRaw = o.id ?? o._id;
  const id = typeof idRaw === "string" ? idRaw : typeof idRaw === "number" ? String(idRaw) : undefined;
  const nom = String(o.vaccin ?? o.libelleVaccin ?? o.typeVaccin ?? "Vaccin");
  const rawDate = String(o.dateVaccination ?? o.dateAdministration ?? o.date ?? "");
  const parsed = rawDate ? new Date(rawDate) : null;
  const date = parsed && !Number.isNaN(parsed.getTime()) ? parsed.toLocaleDateString("fr-FR") : rawDate || "—";
  return {
    id,
    nom,
    date,
    dose: typeof o.dose === "string" ? o.dose : undefined,
    lot: typeof o.lot === "string" ? o.lot : undefined,
    prochaineDose: typeof o.prochaineDose === "string" ? o.prochaineDose : null,
  };
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
      <span className="min-w-[130px] text-xs text-gray-500">{label}</span>
      <span className="text-sm font-medium text-[#0B1F16]">{value || "—"}</span>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#00553E] border-t-transparent" />
    </div>
  );
}

type ArchiveModalProps = {
  nip: string;
  etablissementId: string;
  onClose: () => void;
  onSuccess: () => void;
};

function ArchiveModal({ nip, etablissementId, onClose, onSuccess }: ArchiveModalProps) {
  const [raisons, setRaisons] = useState<RaisonArchivage[]>([]);
  const [loadingRaisons, setLoadingRaisons] = useState(true);
  const [selectedRaisonId, setSelectedRaisonId] = useState("");
  const [motifLibre, setMotifLibre] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    void getRaisonsArchivage().then((res) => {
      if (res.ok) setRaisons(res.data);
    }).finally(() => setLoadingRaisons(false));
  }, []);

  const canSubmit = !!(selectedRaisonId || motifLibre.trim());

  async function handleSubmit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    const res = await archiverPatient(nip, {
      etablissementId,
      raisonArchivageId: selectedRaisonId || undefined,
      motifLibre: motifLibre.trim() || undefined,
    });
    setSubmitting(false);
    if (res.ok) {
      setSuccess(true);
      setTimeout(() => { onSuccess(); }, 1500);
    } else {
      const msg = res.status === 409
        ? "Ce patient est déjà archivé dans votre établissement."
        : res.message;
      setError(msg);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div
        className="w-full max-w-md rounded-[16px] border bg-white shadow-xl"
        style={{ borderColor: "#E5E7EB" }}
      >
        {/* En-tête */}
        <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: "#E5E7EB" }}>
          <h2 className="text-base font-semibold text-[#0B1F16]">Archiver le patient</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {success ? (
            <p className="rounded-[10px] bg-green-50 px-4 py-3 text-sm text-green-800 border border-green-200">
              Patient archivé avec succès.
            </p>
          ) : (
            <>
              {/* NIP + avertissement */}
              <p className="font-mono text-sm text-gray-500">NIP : {nip}</p>
              <p className="rounded-[10px] border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                Cette action archive le dossier. Le professionnel ne pourra plus y accéder sans réactivation.
              </p>

              {/* Raison */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500">
                  Raison d'archivage
                </label>
                {loadingRaisons ? (
                  <div className="h-9 animate-pulse rounded-[8px] bg-gray-100" />
                ) : (
                  <select
                    value={selectedRaisonId}
                    onChange={(e) => setSelectedRaisonId(e.target.value)}
                    className="w-full rounded-[8px] border px-3 py-2 text-sm text-[#0B1F16] outline-none focus:border-[#00553E]"
                    style={{ borderColor: "#D1D5DB" }}
                  >
                    <option value="">— Sélectionner une raison —</option>
                    {raisons.map((r) => (
                      <option key={r._id} value={r._id}>
                        {r.libelle}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Motif libre */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500">
                  Motif libre <span className="text-gray-400">(optionnel si raison choisie)</span>
                </label>
                <textarea
                  value={motifLibre}
                  onChange={(e) => setMotifLibre(e.target.value)}
                  rows={3}
                  placeholder="Précision contextuelle…"
                  className="w-full resize-none rounded-[8px] border px-3 py-2 text-sm text-[#0B1F16] outline-none focus:border-[#00553E]"
                  style={{ borderColor: "#D1D5DB" }}
                />
                {!canSubmit && (
                  <p className="mt-1 text-xs text-red-500">
                    Au moins une raison ou un motif libre est requis.
                  </p>
                )}
              </div>

              {error && (
                <p className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {error}
                </p>
              )}
            </>
          )}
        </div>

        {!success && (
          <div className="flex justify-end gap-3 border-t px-6 py-4" style={{ borderColor: "#E5E7EB" }}>
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-[8px] bg-gray-100 px-4 text-sm font-medium text-[#0B1F16] hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="h-9 rounded-[8px] px-4 text-sm font-medium text-white disabled:opacity-50"
              style={{ background: canSubmit && !submitting ? "#B91C1C" : "#D1D5DB" }}
            >
              {submitting ? "Archivage…" : "Confirmer l'archivage"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EtablissementPatientDetail() {
  const navigate = useNavigate();
  const { nip } = useParams<{ nip: string }>();
  const { establishment } = useAuth();

  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [medFile, setMedFile] = useState<MedFile | null>(null);
  const [medRaw, setMedRaw] = useState<RawMedFile | null>(null);
  const [editingMed, setEditingMed] = useState(false);
  const [medForm, setMedForm] = useState<RawMedFile | null>(null);
  const [newAllergie, setNewAllergie] = useState("");
  const [newIntolerance, setNewIntolerance] = useState("");
  const [savingMed, setSavingMed] = useState(false);
  const [medEditError, setMedEditError] = useState<string | null>(null);
  const [consultations, setConsultations] = useState<ConsultEntry[]>([]);
  const [ordonnances, setOrdonnances] = useState<OrdonnanceEntry[]>([]);
  const [vaccinations, setVaccinations] = useState<VaccinationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [needsAccessOtp, setNeedsAccessOtp] = useState(false);
  const [accessRequiredPatientEstMineur, setAccessRequiredPatientEstMineur] = useState(false);
  const [accessRequiredContacts, setAccessRequiredContacts] = useState<ContactSureteDisponible[]>([]);
  const [accessUrgenceDisponible, setAccessUrgenceDisponible] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState<"identity" | "history" | "ordonnances" | "vaccinations">("identity");
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [dossierPdfLoading, setDossierPdfLoading] = useState(false);
  const [certificatLoading, setCertificatLoading] = useState(false);
  const [consultationPdfLoadingId, setConsultationPdfLoadingId] = useState<string | null>(null);
  const [ordonnancePdfLoadingId, setOrdonnancePdfLoadingId] = useState<string | null>(null);
  const [metiers, setMetiers] = useState<ProfessionMetier[]>([]);

  useEffect(() => {
    let mounted = true;
    void getProfessionsMetiers().then((res) => {
      if (mounted && res.ok) setMetiers(res.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  /** `patient.job` stocke le slug du métier (ex. "cadres_professions_intellectuelles") depuis le
   * correctif du formulaire patient — même référentiel que MedicalFile.tsx côté patient. */
  const jobLabel = patient?.job ? (metiers.find((m) => m.slug === patient.job)?.title ?? patient.job) : "";

  const load = useCallback(async () => {
    if (!nip) return;
    setLoading(true);
    setError(null);
    const [medRes, consultRes, ordoRes, vaccRes] = await Promise.all([
      getPatientMedicalFilePro(nip),
      getPatientConsultationsPro(nip),
      getPatientPrescriptionsPro(nip),
      getPatientVaccinationsPro(nip),
    ]);
    const accessBlocker = [medRes, consultRes, ordoRes, vaccRes].find(
      (r) => !r.ok && (r as { errorCode?: string }).errorCode === "ACCES_DOSSIER_REQUIS"
    ) as { meta?: Record<string, unknown> } | undefined;
    setNeedsAccessOtp(!!accessBlocker);
    setAccessRequiredPatientEstMineur(accessBlocker?.meta?.patientEstMineur === true);
    setAccessRequiredContacts(
      Array.isArray(accessBlocker?.meta?.contactsSureteDisponibles) ? accessBlocker.meta.contactsSureteDisponibles : []
    );
    setAccessUrgenceDisponible(accessBlocker?.meta?.accesUrgenceDisponible === true);

    if (medRes.ok) {
      setPatient(extractPatientFromDossier(medRes.data, nip) ?? minimalPatient(nip));
      setMedFile(extractMedFile(medRes.data));
      const medExiste = (medRes.data as Record<string, unknown> | null)?.existe;
      setMedRaw(medExiste === false ? null : extractRawMedFile(medRes.data));
    } else if (accessRequired) {
      // Accès pas encore accordé : shell minimal (NIP connu depuis l'URL) en attendant l'OTP.
      setPatient(minimalPatient(nip));
      setMedFile(null);
      setMedRaw(null);
    } else {
      setError(medRes.message || "Dossier introuvable ou accès refusé.");
      setPatient(null);
      setMedFile(null);
      setMedRaw(null);
      setLoading(false);
      return;
    }
    setEditingMed(false);

    setConsultations(consultRes.ok ? consultRes.data.map(mapConsult) : []);
    setOrdonnances(ordoRes.ok ? ordoRes.data.map(mapOrdonnance) : []);
    setVaccinations(vaccRes.ok ? vaccRes.data.map(mapVaccination) : []);
    setLoading(false);
  }, [nip]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedConsult = consultations.find((c) => c.selectionKey === selectedKey);

  const handleDownloadDossierPdf = async () => {
    if (!nip) return;
    setDossierPdfLoading(true);
    setActionError(null);
    const res = await getMedicalFilePdfPro(nip);
    setDossierPdfLoading(false);
    if (!res.ok) {
      setActionError(res.message);
      return;
    }
    void downloadFileFromUrl(res.pdfUrl, `dossier-medical-${nip}.pdf`);
  };

  const handleDownloadCertificat = async () => {
    if (!nip) return;
    setCertificatLoading(true);
    setActionError(null);
    const res = await getVaccinationCertificatePro(nip);
    setCertificatLoading(false);
    if (!res.ok) {
      setActionError(res.message);
      return;
    }
    void downloadFileFromUrl(res.pdfUrl, `certificat-vaccination-${nip}.pdf`);
  };

  const handleDownloadConsultationPdf = async (consultationId: string) => {
    setConsultationPdfLoadingId(consultationId);
    setActionError(null);
    const res = await getConsultationPdfPro(consultationId);
    setConsultationPdfLoadingId(null);
    if (!res.ok) {
      setActionError(res.message);
      return;
    }
    void downloadFileFromUrl(res.pdfUrl, `consultation-${consultationId}.pdf`);
  };

  const handleDownloadOrdonnancePdf = async (ordonnanceId: string) => {
    setOrdonnancePdfLoadingId(ordonnanceId);
    setActionError(null);
    const res = await getOrdonnancePdfPro(ordonnanceId);
    setOrdonnancePdfLoadingId(null);
    if (!res.ok) {
      setActionError(res.message);
      return;
    }
    void downloadFileFromUrl(res.pdfUrl, `ordonnance-${ordonnanceId}.pdf`);
  };

  const startEditMed = () => {
    if (!medRaw) return;
    setMedForm(medRaw);
    setNewAllergie("");
    setNewIntolerance("");
    setMedEditError(null);
    setEditingMed(true);
  };

  const cancelEditMed = () => {
    setEditingMed(false);
    setMedEditError(null);
  };

  const updateMedField = <K extends keyof RawMedFile>(key: K, value: RawMedFile[K]) => {
    setMedForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateAntecedent = (index: number, patch: Partial<RawAntecedent>) => {
    setMedForm((prev) => {
      if (!prev) return prev;
      const antecedents = prev.antecedents.map((a, i) => (i === index ? { ...a, ...patch } : a));
      return { ...prev, antecedents };
    });
  };

  const removeAntecedent = (index: number) => {
    setMedForm((prev) => (prev ? { ...prev, antecedents: prev.antecedents.filter((_, i) => i !== index) } : prev));
  };

  const addAllergie = () => {
    const value = newAllergie.trim();
    if (!value) return;
    setMedForm((prev) => (prev ? { ...prev, allergies: [...prev.allergies, value] } : prev));
    setNewAllergie("");
  };

  const removeAllergie = (index: number) => {
    setMedForm((prev) => (prev ? { ...prev, allergies: prev.allergies.filter((_, i) => i !== index) } : prev));
  };

  const addIntolerance = () => {
    const value = newIntolerance.trim();
    if (!value) return;
    setMedForm((prev) => (prev ? { ...prev, intolerances: [...prev.intolerances, value] } : prev));
    setNewIntolerance("");
  };

  const removeIntolerance = (index: number) => {
    setMedForm((prev) => (prev ? { ...prev, intolerances: prev.intolerances.filter((_, i) => i !== index) } : prev));
  };

  const handleSaveMed = async () => {
    if (!nip || !medForm) return;
    setSavingMed(true);
    setMedEditError(null);
    const res = await updateDossierMedicalInfoPro(nip, medForm);
    setSavingMed(false);
    if (!res.ok) {
      setMedEditError(res.message || "Impossible d'enregistrer les modifications.");
      return;
    }
    showToast("Dossier médical mis à jour", "success");
    setEditingMed(false);
    void load();
  };

  const createurLabel = patient ? describeCreateur(patient.createur) : null;

  return (
    <div>
      {showArchiveModal && nip && establishment?.id && (
        <ArchiveModal
          nip={nip}
          etablissementId={establishment.id}
          onClose={() => setShowArchiveModal(false)}
          onSuccess={() => {
            setShowArchiveModal(false);
            navigate("/etablissement/dashboard/patients");
          }}
        />
      )}

      <button
        type="button"
        onClick={() => navigate("/etablissement/dashboard/patients")}
        className="mb-5 flex items-center gap-2 text-sm font-medium text-[#00553E] hover:underline"
      >
        <ArrowLeft size={16} /> Retour aux patients
      </button>

      {actionError && (
        <div className="mb-4 rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          {actionError}
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          {error}
        </div>
      ) : patient ? (
        <div className="space-y-5">
          {/* En-tête */}
          <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-[#0B1F16]">
                  {patient.nom || patient.prenom ? `${patient.nom} ${patient.prenom}`.trim() : "Patient"}
                </h1>
                <p className="mt-0.5 font-mono text-sm text-gray-500">NIP : {patient.nip}</p>
                {needsAccessOtp && (
                  <p className="mt-0.5 text-xs text-amber-700">En attente d'autorisation d'accès du patient…</p>
                )}
                {createurLabel && (
                  <p className="mt-0.5 text-xs text-gray-400">{createurLabel}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {patient.blood && (
                  <span
                    className="rounded-full border px-3 py-1 text-sm font-semibold text-[#00553E]"
                    style={{ borderColor: "#00553E" }}
                  >
                    {patient.blood}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => void handleDownloadDossierPdf()}
                  disabled={dossierPdfLoading}
                  className="flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-sm font-medium text-[#0B1F16] hover:bg-gray-50 disabled:opacity-60"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  <Download size={14} />
                  {dossierPdfLoading ? "Génération…" : "Dossier (PDF)"}
                </button>
                {establishment?.id && (
                  <button
                    type="button"
                    onClick={() => setShowArchiveModal(true)}
                    className="flex items-center gap-1.5 rounded-[8px] border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
                  >
                    <Archive size={14} />
                    Archiver
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Onglets */}
          <div className="flex flex-wrap gap-2">
            {([
              { id: "identity", label: "Identité & Dossier" },
              { id: "history", label: `Consultations (${consultations.length})` },
              { id: "ordonnances", label: `Ordonnances (${ordonnances.length})` },
              { id: "vaccinations", label: `Vaccinations (${vaccinations.length})` },
            ] as const).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className="h-9 rounded-[10px] px-4 text-sm font-medium"
                style={{
                  background: tab === t.id ? "#00553E" : "#E5E7EB",
                  color: tab === t.id ? "#fff" : "#0B1F16",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Onglet identité + dossier */}
          {tab === "identity" && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div
                className="space-y-3 rounded-[12px] border bg-white p-5"
                style={{ borderColor: "#E5E7EB" }}
              >
                <h2 className="mb-3 text-sm font-semibold text-[#0B1F16]">
                  Informations personnelles
                </h2>
                <InfoRow label="NIP" value={patient.nip} />
                <InfoRow label="E-mail" value={patient.email} />
                <InfoRow label="Téléphone" value={patient.phone} />
                <InfoRow label="Date de naissance" value={formatDateFr(patient.birth)} />
                <InfoRow label="Âge" value={patient.age} />
                <InfoRow label="Adresse" value={patient.address} />
                <InfoRow label="Profession" value={jobLabel} />
                <InfoRow label="Groupe sanguin" value={patient.blood} />
              </div>

              <div
                className="space-y-4 rounded-[12px] border bg-white p-5"
                style={{ borderColor: "#E5E7EB" }}
              >
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-[#0B1F16]">Dossier médical</h2>
                  {medFile && !editingMed && (
                    <button
                      type="button"
                      onClick={startEditMed}
                      className="flex items-center gap-1 text-xs font-medium text-[#00553E] hover:underline"
                    >
                      <Pencil size={12} /> Modifier
                    </button>
                  )}
                </div>

                {editingMed && medForm ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-xs text-gray-500">Groupe sanguin</label>
                        <Select
                          value={medForm.groupeSanguin || UNSET_OPTION}
                          onValueChange={(v) => updateMedField("groupeSanguin", v === UNSET_OPTION ? "" : v)}
                        >
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UNSET_OPTION}>—</SelectItem>
                            {withCurrentValue(BLOOD_GROUP_OPTIONS, medForm.groupeSanguin).map((g) => (
                              <SelectItem key={g} value={g}>{g}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-gray-500">Électrophorèse</label>
                        <Select
                          value={medForm.electrophorese || UNSET_OPTION}
                          onValueChange={(v) => updateMedField("electrophorese", v === UNSET_OPTION ? "" : v)}
                        >
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UNSET_OPTION}>—</SelectItem>
                            {withCurrentValue(ELECTROPHORESE_OPTIONS, medForm.electrophorese).map((opt) => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-gray-500">Poids (kg)</label>
                        <input
                          type="number"
                          value={medForm.poids ?? ""}
                          onChange={(e) => updateMedField("poids", e.target.value === "" ? null : Number(e.target.value))}
                          className="h-9 w-full rounded-[8px] border px-3 text-sm outline-none focus:border-[#00553E]"
                          style={{ borderColor: "#D1D5DB" }}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-gray-500">Taille (cm)</label>
                        <input
                          type="number"
                          value={medForm.taille ?? ""}
                          onChange={(e) => updateMedField("taille", e.target.value === "" ? null : Number(e.target.value))}
                          className="h-9 w-full rounded-[8px] border px-3 text-sm outline-none focus:border-[#00553E]"
                          style={{ borderColor: "#D1D5DB" }}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs text-gray-500">Allergies</label>
                      {medForm.allergies.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1.5">
                          {medForm.allergies.map((a, i) => (
                            <span
                              key={i}
                              className="flex items-center gap-1 rounded-full bg-red-100 py-0.5 pl-2 pr-1 text-xs text-red-700"
                            >
                              {a}
                              <button
                                type="button"
                                onClick={() => removeAllergie(i)}
                                aria-label={`Supprimer l'allergie ${a}`}
                                className="rounded-full p-0.5 hover:bg-red-200"
                              >
                                <X size={10} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input
                          value={newAllergie}
                          onChange={(e) => setNewAllergie(e.target.value.toUpperCase())}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); addAllergie(); }
                          }}
                          placeholder="Ajouter une allergie"
                          className="h-9 flex-1 rounded-[8px] border px-3 text-sm outline-none focus:border-[#00553E]"
                          style={{ borderColor: "#D1D5DB" }}
                        />
                        <button
                          type="button"
                          onClick={addAllergie}
                          disabled={!newAllergie.trim()}
                          className="flex h-9 w-9 items-center justify-center rounded-[8px] border text-[#00553E] disabled:opacity-40"
                          style={{ borderColor: "#D1D5DB" }}
                          aria-label="Ajouter l'allergie"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs text-gray-500">Intolérances</label>
                      {medForm.intolerances.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1.5">
                          {medForm.intolerances.map((t, i) => (
                            <span
                              key={i}
                              className="flex items-center gap-1 rounded-full bg-blue-100 py-0.5 pl-2 pr-1 text-xs text-blue-700"
                            >
                              {t}
                              <button
                                type="button"
                                onClick={() => removeIntolerance(i)}
                                aria-label={`Supprimer l'intolérance ${t}`}
                                className="rounded-full p-0.5 hover:bg-blue-200"
                              >
                                <X size={10} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input
                          value={newIntolerance}
                          onChange={(e) => setNewIntolerance(e.target.value.toUpperCase())}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); addIntolerance(); }
                          }}
                          placeholder="Ajouter une intolérance"
                          className="h-9 flex-1 rounded-[8px] border px-3 text-sm outline-none focus:border-[#00553E]"
                          style={{ borderColor: "#D1D5DB" }}
                        />
                        <button
                          type="button"
                          onClick={addIntolerance}
                          disabled={!newIntolerance.trim()}
                          className="flex h-9 w-9 items-center justify-center rounded-[8px] border text-[#00553E] disabled:opacity-40"
                          style={{ borderColor: "#D1D5DB" }}
                          aria-label="Ajouter l'intolérance"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>

                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <span className="text-xs text-gray-500">Antécédents</span>
                        <button
                          type="button"
                          onClick={() =>
                            setMedForm((prev) =>
                              prev ? { ...prev, antecedents: [...prev.antecedents, emptyAntecedent()] } : prev
                            )
                          }
                          className="flex items-center gap-1 text-xs font-medium text-[#00553E] hover:underline"
                        >
                          <Plus size={12} /> Ajouter
                        </button>
                      </div>
                      {medForm.antecedents.length === 0 && (
                        <p className="text-xs text-gray-400">Aucun antécédent.</p>
                      )}
                      <div className="space-y-2">
                        {medForm.antecedents.map((a, i) => (
                          <div key={i} className="grid grid-cols-1 gap-2 rounded-[8px] border p-2 sm:grid-cols-[1fr_2fr_auto_auto]" style={{ borderColor: "#E5E7EB" }}>
                            <input
                              placeholder="Type"
                              value={a.type}
                              onChange={(e) => updateAntecedent(i, { type: e.target.value.toUpperCase() })}
                              className="h-8 rounded-[6px] border px-2 text-xs outline-none focus:border-[#00553E]"
                              style={{ borderColor: "#D1D5DB" }}
                            />
                            <input
                              placeholder="Description"
                              value={a.description}
                              onChange={(e) => updateAntecedent(i, { description: e.target.value.toUpperCase() })}
                              className="h-8 rounded-[6px] border px-2 text-xs outline-none focus:border-[#00553E]"
                              style={{ borderColor: "#D1D5DB" }}
                            />
                            <input
                              type="date"
                              value={dateInputValue(a.date)}
                              onChange={(e) => updateAntecedent(i, { date: e.target.value || null })}
                              className="h-8 rounded-[6px] border px-2 text-xs outline-none focus:border-[#00553E]"
                              style={{ borderColor: "#D1D5DB" }}
                            />
                            <button
                              type="button"
                              onClick={() => removeAntecedent(i)}
                              className="flex items-center justify-center rounded-[6px] text-red-500 hover:bg-red-50"
                              aria-label="Supprimer l'antécédent"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {medEditError && (
                      <p className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                        {medEditError}
                      </p>
                    )}

                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={cancelEditMed}
                        disabled={savingMed}
                        className="h-9 rounded-[8px] border px-4 text-xs font-medium text-[#0B1F16] disabled:opacity-50"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleSaveMed()}
                        disabled={savingMed}
                        className="h-9 rounded-[8px] px-4 text-xs font-semibold text-white disabled:opacity-60"
                        style={{ background: "#00553E" }}
                      >
                        {savingMed ? "Enregistrement…" : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                ) : medFile ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <InfoRow label="Groupe sanguin" value={medFile.groupeSanguin} />
                      <InfoRow label="Électrophorèse" value={medFile.electrophorese} />
                      <InfoRow label="Poids" value={medFile.poids != null ? `${medFile.poids} kg` : ""} />
                      <InfoRow label="Taille" value={medFile.taille != null ? `${medFile.taille} cm` : ""} />
                    </div>
                    {medFile.antecedents.length > 0 && (
                      <div>
                        <p className="mb-1 text-xs text-gray-500">Antécédents</p>
                        <div className="flex flex-wrap gap-1.5">
                          {medFile.antecedents.map((a, i) => (
                            <span key={i} className="rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-700">
                              {a}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {medFile.allergies.length > 0 && (
                      <div>
                        <p className="mb-1 text-xs text-gray-500">Allergies</p>
                        <div className="flex flex-wrap gap-1.5">
                          {medFile.allergies.map((a, i) => (
                            <span key={i} className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                              {a}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {medFile.intolerances.length > 0 && (
                      <div>
                        <p className="mb-1 text-xs text-gray-500">Intolérances</p>
                        <div className="flex flex-wrap gap-1.5">
                          {medFile.intolerances.map((t, i) => (
                            <span key={i} className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {!medFile.groupeSanguin &&
                      medFile.poids == null &&
                      medFile.taille == null &&
                      medFile.antecedents.length === 0 &&
                      medFile.allergies.length === 0 &&
                      medFile.intolerances.length === 0 && (
                        <p className="text-sm text-gray-400">Dossier médical vide.</p>
                      )}
                  </>
                ) : (
                  <p className="text-sm text-gray-400">
                    {needsAccessOtp
                      ? "Dossier médical non accessible tant que l'autorisation du patient n'est pas validée."
                      : "Dossier médical non disponible."}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Onglet historique consultations */}
          {tab === "history" && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              {/* Liste consultations */}
              <div
                className="overflow-hidden rounded-[12px] border bg-white lg:col-span-1"
                style={{ borderColor: "#E5E7EB" }}
              >
                {consultations.length === 0 ? (
                  <p className="px-5 py-8 text-center text-sm text-gray-400">
                    Aucune consultation enregistrée.
                  </p>
                ) : (
                  <div className="divide-y" style={{ borderColor: "#F3F4F6" }}>
                    {consultations.map((c) => (
                      <button
                        key={c.selectionKey}
                        type="button"
                        onClick={() => setSelectedKey(c.selectionKey)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50"
                        style={{
                          background: selectedKey === c.selectionKey ? "#DCEFE9" : undefined,
                        }}
                      >
                        <p className="text-sm font-medium text-[#0B1F16]">{c.label}</p>
                        <p className="text-xs text-gray-500">Médecin : {c.doctor}</p>
                        {c.statut && (
                          <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                            {c.statut}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Détail consultation sélectionnée */}
              <div
                className="rounded-[12px] border bg-white p-5 lg:col-span-2"
                style={{ borderColor: "#E5E7EB" }}
              >
                {selectedConsult ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h2 className="text-base font-semibold text-[#0B1F16]">{selectedConsult.label}</h2>
                      {selectedConsult.id && (
                        <button
                          type="button"
                          onClick={() => void handleDownloadConsultationPdf(selectedConsult.id as string)}
                          disabled={consultationPdfLoadingId === selectedConsult.id}
                          className="flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-xs font-medium text-[#0B1F16] hover:bg-gray-50 disabled:opacity-60"
                          style={{ borderColor: "#E5E7EB" }}
                        >
                          <Download size={12} />
                          {consultationPdfLoadingId === selectedConsult.id ? "Génération…" : "Fiche (PDF)"}
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <InfoRow label="Médecin (NIP)" value={selectedConsult.doctor} />
                      {selectedConsult.statut && (
                        <InfoRow label="Statut" value={selectedConsult.statut} />
                      )}
                      {selectedConsult.etatSortie && (
                        <InfoRow label="État de sortie" value={selectedConsult.etatSortie} />
                      )}
                    </div>
                    {selectedConsult.motif && (
                      <div>
                        <p className="mb-1 text-xs text-gray-500">Motif</p>
                        <p className="text-sm text-[#0B1F16]">{selectedConsult.motif}</p>
                      </div>
                    )}
                    {selectedConsult.observations && (
                      <div>
                        <p className="mb-1 text-xs text-gray-500">Observations</p>
                        <p className="whitespace-pre-wrap text-sm text-[#0B1F16]">
                          {selectedConsult.observations}
                        </p>
                      </div>
                    )}
                    {selectedConsult.analyses && selectedConsult.analyses.length > 0 && (
                      <div>
                        <p className="mb-1 text-xs text-gray-500">Analyses</p>
                        <ul className="list-disc pl-5 text-sm text-[#0B1F16] space-y-1">
                          {selectedConsult.analyses.map((a, i) => (
                            <li key={i}>{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedConsult.resultats && (
                      <div>
                        <p className="mb-1 text-xs text-gray-500">Diagnostic / Résultats</p>
                        <p className="text-sm font-medium text-[#0B1F16]">{selectedConsult.resultats.disease}</p>
                        {selectedConsult.resultats.note && (
                          <p className="mt-1 whitespace-pre-wrap text-sm text-[#0B1F16]/80">
                            {selectedConsult.resultats.note}
                          </p>
                        )}
                      </div>
                    )}
                    {selectedConsult.ordonnances && selectedConsult.ordonnances.length > 0 && (
                      <div>
                        <p className="mb-1 text-xs text-gray-500">Ordonnance</p>
                        <ul className="space-y-1 text-sm text-[#0B1F16]">
                          {selectedConsult.ordonnances.map((row, i) => (
                            <li key={i}>
                              <span className="font-medium">{row.medicament}</span>
                              {row.note && <span className="text-[#0B1F16]/70"> — {row.note}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedConsult.documents && selectedConsult.documents.length > 0 && (
                      <div>
                        <p className="mb-1 text-xs text-gray-500">Pièces jointes</p>
                        <ul className="space-y-2">
                          {selectedConsult.documents.map((doc, idx) => (
                            <li key={idx}>
                              <FileAttachmentLink file={doc} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="py-8 text-center text-sm text-gray-400">
                    {consultations.length > 0
                      ? "Sélectionnez une consultation dans la liste."
                      : "Aucune consultation."}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Onglet ordonnances */}
          {tab === "ordonnances" && (
            <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
              {ordonnances.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">Aucune ordonnance enregistrée.</p>
              ) : (
                <div className="space-y-3">
                  {ordonnances.map((o, idx) => (
                    <div
                      key={o.id ?? idx}
                      className="rounded-[10px] border p-4"
                      style={{ borderColor: "#E5E7EB" }}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-[#0B1F16]">{o.label}</p>
                          {o.statut && (
                            <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                              {o.statut}
                            </span>
                          )}
                        </div>
                        {o.id && (
                          <button
                            type="button"
                            onClick={() => void handleDownloadOrdonnancePdf(o.id as string)}
                            disabled={ordonnancePdfLoadingId === o.id}
                            className="flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-xs font-medium text-[#0B1F16] hover:bg-gray-50 disabled:opacity-60"
                            style={{ borderColor: "#E5E7EB" }}
                          >
                            <Download size={12} />
                            {ordonnancePdfLoadingId === o.id ? "Génération…" : "Télécharger (PDF)"}
                          </button>
                        )}
                      </div>
                      {o.medicaments.length > 0 && (
                        <ul className="mt-3 space-y-1 text-sm text-[#0B1F16]">
                          {o.medicaments.map((m, i) => (
                            <li key={i}>
                              <span className="font-medium">{m.medicament}</span>
                              {m.note && <span className="text-[#0B1F16]/70"> — {m.note}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Onglet vaccinations */}
          {tab === "vaccinations" && (
            <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
              <div className="mb-4 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-[#0B1F16]">Vaccinations</h2>
                <button
                  type="button"
                  onClick={() => void handleDownloadCertificat()}
                  disabled={certificatLoading}
                  className="flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-xs font-medium text-[#0B1F16] hover:bg-gray-50 disabled:opacity-60"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  <Download size={12} />
                  {certificatLoading ? "Génération…" : "Certificat (PDF)"}
                </button>
              </div>
              {vaccinations.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">Aucune vaccination enregistrée.</p>
              ) : (
                <div className="space-y-2">
                  {vaccinations.map((v, idx) => (
                    <div
                      key={v.id ?? idx}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-[10px] border p-3"
                      style={{ borderColor: "#E5E7EB" }}
                    >
                      <div className="font-medium text-[#0B1F16]">{v.nom}</div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                        {v.dose && <span>Dose : {v.dose}</span>}
                        {v.lot && <span>Lot : {v.lot}</span>}
                        <span>{v.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}

      <PatientAccessOtpModal
        open={needsAccessOtp}
        patientNip={patient?.nip || (nip ? decodeURIComponent(nip) : "")}
        patientDisplayName={patient ? [patient.prenom, patient.nom].filter(Boolean).join(" ") || undefined : undefined}
        estMineur={accessRequiredPatientEstMineur}
        contactsSureteDisponibles={accessRequiredContacts}
        accesUrgenceDisponible={accessUrgenceDisponible}
        onClose={() => {
          setNeedsAccessOtp(false);
          navigate("/etablissement/dashboard/patients");
        }}
        onSuccess={() => {
          setNeedsAccessOtp(false);
          void load();
        }}
      />
    </div>
  );
}
